import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ShieldCheck, Download, Search, Edit3, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Pagination, { usePagination } from "@/components/Pagination";

export default function StatusOptions() {
  const { hasPerm } = useAuth();
  const [opts, setOpts] = useState([]);
  const [label, setLabel] = useState("");
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const canCreate = hasPerm("status_options", "create");
  const canEdit = hasPerm("status_options", "edit");
  const canDelete = hasPerm("status_options", "delete");

  const load = async () => {
    const { data } = await api.get("/status-options");
    setOpts(data);
  };

  useEffect(() => { load(); }, []);

  const filteredOpts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return opts;
    return opts.filter((o) => (o.label || "").toLowerCase().includes(needle));
  }, [opts, q]);

  const {
    pageItems: pagedOpts,
    page: soPage, setPage: setSoPage,
    pageSize: soPageSize, setPageSize: setSoPageSize,
    totalPages: soTotalPages, totalItems: soTotalItems,
  } = usePagination(filteredOpts, "voltledger.status.pageSize");

  const add = async () => {
    if (!label.trim()) return;
    try {
      await api.post("/status-options", { label: label.trim() });
      setLabel("");
      toast.success("Added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this status option? Existing invoices using it will retain the label.")) return;
    try {
      await api.delete(`/status-options/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setEditLabel(o.label);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
  };

  const saveEdit = async (o) => {
    const next = editLabel.trim();
    if (!next) return toast.error("Label cannot be empty");
    if (next === o.label) {
      cancelEdit();
      return;
    }
    try {
      await api.put(`/status-options/${o.id}`, { label: next });
      toast.success("Renamed (invoices using this label were updated too)");
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const toggleActive = async (o) => {
    const nextActive = !(o.active !== false);
    try {
      await api.put(`/status-options/${o.id}`, { active: nextActive });
      toast.success(nextActive ? "Status activated" : "Status deactivated · hidden from invoice dropdown");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get("/exports/status-options", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `status_options_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Status options exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[960px]">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">Administration</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Invoice Status Options</h1>
          <p className="text-sm text-slate-500 mt-2">
            Customise the dropdown values used to mark invoice status. Inactive options are hidden from the invoice dropdown but historical invoices keep their label.
            {q && <span className="ml-2 text-indigo-600 font-semibold">· {filteredOpts.length} match{filteredOpts.length === 1 ? "" : "es"}</span>}
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Search label…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 pr-9 h-10 text-sm border-slate-300 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
              data-testid="status-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 text-xs font-semibold"
                data-testid="status-search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <Button onClick={downloadExcel} variant="outline" className="rounded-none h-10 border-slate-300" data-testid="export-status-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export Excel
          </Button>
        </div>
      </div>

      {canCreate && (
        <div className="bg-white border border-slate-200 p-5 mb-6 flex gap-3">
          <Input value={label} onChange={(e)=>setLabel(e.target.value)} placeholder="New status label" className="rounded-none" data-testid="new-status-input" onKeyDown={(e)=>e.key==="Enter" && add()} />
          <Button onClick={add} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="add-status-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        </div>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-12">#</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Label</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-28">Type</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-32">Active</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOpts.length === 0 && (
              <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-12">{q ? `No status matches "${q}".` : "No status options yet."}</td></tr>
            )}
            {pagedOpts.map((o, idx) => {
              const isEditing = editingId === o.id;
              const isActive = o.active !== false;
              return (
                <tr key={o.id} className={`border-b border-slate-100 ${!isActive ? "bg-slate-50/60" : ""}`} data-testid={`status-row-${o.label}`}>
                  <td className="px-4 py-3 text-slate-400 font-mono-data">{String(idx + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <Input
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(o);
                          if (e.key === "Escape") cancelEdit();
                        }}
                        autoFocus
                        className="rounded-none h-8 text-sm"
                        data-testid={`status-edit-input-${o.label}`}
                      />
                    ) : (
                      <span className={`font-medium ${isActive ? "text-slate-900" : "text-slate-400 line-through"}`}>{o.label}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {o.is_system ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#002FA7]"><ShieldCheck className="w-3 h-3" /> System</span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Custom</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={isActive}
                        onCheckedChange={() => canEdit && toggleActive(o)}
                        disabled={!canEdit || isEditing}
                        data-testid={`status-active-toggle-${o.label}`}
                      />
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${isActive ? "text-emerald-700" : "text-slate-400"}`}>
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isEditing ? (
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => saveEdit(o)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900" data-testid={`status-edit-save-${o.label}`}>
                          <Check className="w-4 h-4" /> Save
                        </button>
                        <button onClick={cancelEdit} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid={`status-edit-cancel-${o.label}`}>
                          <X className="w-4 h-4" /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-3">
                        {canEdit && (
                          <button onClick={() => startEdit(o)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#002FA7]" data-testid={`status-edit-${o.label}`}>
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </button>
                        )}
                        {canDelete && !o.is_system && (
                          <button onClick={() => del(o.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700" data-testid={`del-status-${o.label}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={soPage} setPage={setSoPage}
          pageSize={soPageSize} setPageSize={setSoPageSize}
          totalPages={soTotalPages} totalItems={soTotalItems}
          testId="status-pagination"
        />
      </div>
    </div>
  );
}
